import { useRef, useState } from 'react';
import { useTracker } from '../../state/store';
import { audioEngine } from '../../engine/audio';
import { loadModule } from '../../engine/loaders';
import { saveMod } from '../../engine/export/modWriter';
import { saveXm } from '../../engine/export/xmWriter';
import { encodeWav, renderSong } from '../../engine/export/wav';
import { Button, Field, NumberInput, Panel, TextInput } from './primitives';

function download(data: Uint8Array, filename: string, mime: string): void {
  const blob = new Blob([data as unknown as BlobPart], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  // Revoking immediately can cancel the download in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

function baseName(name: string): string {
  return name.replace(/\.[^.]+$/, '') || 'untitled';
}

export function SongPanel() {
  const song = useTracker((s) => s.song);
  const fileName = useTracker((s) => s.fileName);
  const warnings = useTracker((s) => s.warnings);
  const revision = useTracker((s) => s.revision);

  const loadSong = useTracker((s) => s.loadSong);
  const newSong = useTracker((s) => s.newSong);
  const setSongName = useTracker((s) => s.setSongName);
  const setInitialSpeed = useTracker((s) => s.setInitialSpeed);
  const setInitialTempo = useTracker((s) => s.setInitialTempo);
  const setRestartPosition = useTracker((s) => s.setRestartPosition);

  const fileRef = useRef<HTMLInputElement>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [saveWarnings, setSaveWarnings] = useState<string[]>([]);

  const openFile = async (file: File) => {
    setStatus('loading…');
    setSaveWarnings([]);
    try {
      const buffer = await file.arrayBuffer();
      const { song: loaded, warnings: loadWarnings } = loadModule(buffer);
      await audioEngine.init();
      loadSong(loaded, file.name, loadWarnings);
      setStatus(`loaded ${loaded.channels}ch ${loaded.format.toUpperCase()} · ${loaded.patterns.length} patterns`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'failed to load');
    }
  };

  const doSaveXm = () => {
    try {
      const { data, warnings: w } = saveXm(song);
      setSaveWarnings(w);
      download(data, `${baseName(fileName)}.xm`, 'application/octet-stream');
      setStatus(`saved ${(data.length / 1024).toFixed(0)} KB`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'save failed');
    }
  };

  const doSaveMod = () => {
    try {
      const { data, warnings: w } = saveMod(song);
      setSaveWarnings(w);
      download(data, `${baseName(fileName)}.mod`, 'application/octet-stream');
      setStatus(`saved ${(data.length / 1024).toFixed(0)} KB`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'save failed');
    }
  };

  const doRenderWav = async () => {
    setStatus('rendering…');
    setSaveWarnings([]);
    // Let the browser paint the status before the render blocks the thread.
    await new Promise((resolve) => setTimeout(resolve, 20));
    try {
      const mixer = useTracker.getState().mixerSettings;
      const audio = renderSong(song, { sampleRate: 44100, mixer, maxSeconds: 900 });
      const wav = encodeWav(audio, 16);
      download(wav, `${baseName(fileName)}.wav`, 'audio/wav');
      setStatus(`rendered ${audio.seconds.toFixed(1)}s`);
    } catch (error) {
      setStatus(error instanceof Error ? error.message : 'render failed');
    }
  };

  return (
    <Panel title="Song">
      <div key={revision} className="flex flex-col gap-2">
        <div className="flex flex-wrap gap-1">
          <Button onClick={() => fileRef.current?.click()} tone="accent">
            Open…
          </Button>
          <Button onClick={newSong}>New</Button>
          <Button onClick={doSaveXm} title="Save as FastTracker II module">
            Save XM
          </Button>
          <Button onClick={doSaveMod} title="Save as ProTracker module (lossy)">
            Save MOD
          </Button>
          <Button onClick={doRenderWav} title="Render the whole song to a WAV file">
            Render WAV
          </Button>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".mod,.xm,.MOD,.XM"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void openFile(file);
            e.target.value = '';
          }}
        />

        {status && <p className="font-mono text-[10px] text-acid-400">{status}</p>}

        <Field label="Title">
          <TextInput value={song.name} onChange={setSongName} className="w-40" />
        </Field>
        <Field label="Speed (ticks/row)">
          <NumberInput value={song.initialSpeed} min={1} max={31} onChange={setInitialSpeed} />
        </Field>
        <Field label="Tempo (BPM)">
          <NumberInput value={song.initialTempo} min={32} max={255} onChange={setInitialTempo} />
        </Field>
        <Field label="Restart position">
          <NumberInput
            value={song.restartPosition}
            min={0}
            max={Math.max(0, song.songLength - 1)}
            onChange={setRestartPosition}
          />
        </Field>

        <div className="mt-1 border-t border-ink-700 pt-2 font-mono text-[10px] leading-relaxed text-ink-500">
          <div>
            Format <span className="text-ink-300">{song.format.toUpperCase()}</span> ·{' '}
            {song.trackerName || 'unknown'}
          </div>
          <div>
            {song.channels} channels · {song.patterns.length} patterns · {song.instruments.length}{' '}
            instruments
          </div>
          <div>
            Pitch{' '}
            <span className="text-ink-300">
              {song.mode === 'pt' ? 'Amiga (PAL)' : song.linearFrequency ? 'linear' : 'Amiga'}
            </span>
          </div>
        </div>

        {warnings.length > 0 && (
          <details className="rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1">
            <summary className="cursor-pointer font-mono text-[10px] text-amber-400">
              {warnings.length} load note{warnings.length === 1 ? '' : 's'}
            </summary>
            <ul className="mt-1 space-y-0.5">
              {warnings.map((w, i) => (
                <li key={i} className="font-mono text-[10px] leading-relaxed text-amber-200/70">
                  {w.message}
                </li>
              ))}
            </ul>
          </details>
        )}

        {saveWarnings.length > 0 && (
          <details open className="rounded border border-amber-400/30 bg-amber-400/5 px-2 py-1">
            <summary className="cursor-pointer font-mono text-[10px] text-amber-400">
              {saveWarnings.length} thing{saveWarnings.length === 1 ? '' : 's'} changed on save
            </summary>
            <ul className="mt-1 space-y-0.5">
              {saveWarnings.map((w, i) => (
                <li key={i} className="font-mono text-[10px] leading-relaxed text-amber-200/70">
                  {w}
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </Panel>
  );
}
